import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listItems } from '../api/masterData';
import { listSuppliers } from '../api/masterData';
import { usePermissions } from '../context/PermissionContext';
import { IconBox, IconDashboard, IconSearch, IconUsers } from './icons';
import type { Item, PermissionResource, Supplier } from '../types/domain';

interface RouteEntry {
  label: string;
  path: string;
  keywords: string;
  /** Resource that must be readable for this route to appear. Omit for routes with no read gate (e.g. Dashboard). */
  requires?: PermissionResource;
}

const ROUTES: RouteEntry[] = [
  { label: 'Dashboard', path: '/app/dashboard', keywords: 'home beranda' },
  { label: 'Items', path: '/app/items', keywords: 'barang master item', requires: 'item' },
  { label: 'Suppliers', path: '/app/suppliers', keywords: 'pemasok vendor', requires: 'supplier' },
  { label: 'Purchase Requests', path: '/app/purchase-requests', keywords: 'pr permintaan pembelian', requires: 'purchase_request' },
  { label: 'Purchase Orders', path: '/app/purchase-orders', keywords: 'po pesanan pembelian', requires: 'purchase_order' },
  { label: 'Goods Receipts', path: '/app/goods-receipts', keywords: 'gr penerimaan barang', requires: 'goods_receipt' },
  { label: 'Stock Ledger', path: '/app/stock-ledger', keywords: 'kartu stok pergerakan', requires: 'stock_ledger' },
  { label: 'Stock Balance', path: '/app/stock-balance', keywords: 'saldo stok', requires: 'stock_balance' },
  { label: 'Stock Opname', path: '/app/stock-opname', keywords: 'hitung fisik opname', requires: 'stock_count' },
  { label: 'Transfers', path: '/app/transfers', keywords: 'transfer pindah gudang', requires: 'transfer' },
  { label: 'Waste', path: '/app/waste', keywords: 'rusak buang kadaluarsa', requires: 'waste' },
  { label: 'Expiry / Lot', path: '/app/expiry', keywords: 'lot batch kadaluarsa expiry', requires: 'expiry_lot' },
];

/**
 * Global quick-search (Cmd/Ctrl+K or the topbar search icon). Real
 * behavior, not a decorative modal: filters the actual route list — only
 * routes whose resource the current user can read — plus a live
 * substring match against items/suppliers already fetched from Supabase
 * (RLS-scoped, same data the Items/Suppliers pages show, and only fetched
 * at all if the user can read that resource). Arrow keys move the
 * selection, Enter opens the selected/first result, Escape or a click
 * outside closes — selecting a result navigates there for real via
 * react-router.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Item[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const canReadItem = hasPermission('item', 'read');
  const canReadSupplier = hasPermission('supplier', 'read');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    setTimeout(() => inputRef.current?.focus(), 10);
    if (canReadItem && items === null) {
      listItems()
        .then(setItems)
        .catch(() => setItems([]));
    }
    if (canReadSupplier && suppliers === null) {
      listSuppliers()
        .then(setSuppliers)
        .catch(() => setSuppliers([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canReadItem, canReadSupplier]);

  const q = query.trim().toLowerCase();
  const visibleRoutes = useMemo(() => ROUTES.filter((r) => !r.requires || hasPermission(r.requires, 'read')), [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const matchedRoutes = useMemo(() => (q ? visibleRoutes.filter((r) => `${r.label} ${r.keywords}`.toLowerCase().includes(q)) : visibleRoutes).slice(0, 6), [q, visibleRoutes]);
  const matchedItems = useMemo(
    () => (q && items ? items.filter((i) => `${i.item_code} ${i.name}`.toLowerCase().includes(q)).slice(0, 5) : []),
    [q, items]
  );
  const matchedSuppliers = useMemo(
    () => (q && suppliers ? suppliers.filter((s) => `${s.supplier_code} ${s.name}`.toLowerCase().includes(q)).slice(0, 5) : []),
    [q, suppliers]
  );

  type FlatResult = { key: string; path: string };
  const flatResults: FlatResult[] = useMemo(
    () => [
      ...matchedRoutes.map((r) => ({ key: `route-${r.path}`, path: r.path })),
      ...matchedItems.map((i) => ({ key: `item-${i.item_id}`, path: '/app/items' })),
      ...matchedSuppliers.map((s) => ({ key: `supplier-${s.supplier_id}`, path: '/app/suppliers' })),
    ],
    [matchedRoutes, matchedItems, matchedSuppliers]
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const target = flatResults[activeIndex];
        if (target) go(target.path);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, flatResults, activeIndex]);

  if (!open) return null;

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  let flatCursor = -1;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.35)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="alex-card"
        style={{ width: 560, maxWidth: '90vw', maxHeight: '65vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--alex-shadow-popover)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--alex-border-soft)' }}>
          <IconSearch size={16} color="var(--alex-text-faint)" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari halaman, item, atau supplier..."
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 'var(--alex-fs-body)', fontFamily: 'var(--alex-font-family)', height: 28 }}
          />
          <kbd style={{ fontSize: 10, color: 'var(--alex-text-faint)', border: '1px solid var(--alex-border)', borderRadius: 4, padding: '1px 5px' }}>Esc</kbd>
        </div>
        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          <ResultGroup title="Halaman">
            {matchedRoutes.map((r) => {
              flatCursor += 1;
              return <ResultRow key={r.path} icon={<IconDashboard size={15} />} label={r.label} active={flatCursor === activeIndex} onClick={() => go(r.path)} />;
            })}
            {matchedRoutes.length === 0 && <NoMatch />}
          </ResultGroup>
          {q && (
            <ResultGroup title="Items">
              {matchedItems.map((it) => {
                flatCursor += 1;
                return <ResultRow key={it.item_id} icon={<IconBox size={15} />} label={`${it.item_code} — ${it.name}`} active={flatCursor === activeIndex} onClick={() => go('/app/items')} />;
              })}
              {matchedItems.length === 0 && <NoMatch />}
            </ResultGroup>
          )}
          {q && (
            <ResultGroup title="Suppliers">
              {matchedSuppliers.map((s) => {
                flatCursor += 1;
                return <ResultRow key={s.supplier_id} icon={<IconUsers size={15} />} label={`${s.supplier_code} — ${s.name}`} active={flatCursor === activeIndex} onClick={() => go('/app/suppliers')} />;
              })}
              {matchedSuppliers.length === 0 && <NoMatch />}
            </ResultGroup>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--alex-text-faint)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '6px 14px 2px' }}>{title}</div>
      {children}
    </div>
  );
}

function ResultRow({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--alex-surface-subtle)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = active ? 'var(--alex-surface-subtle)' : 'none')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        padding: '8px 14px',
        border: 'none',
        background: active ? 'var(--alex-surface-subtle)' : 'none',
        fontSize: 'var(--alex-fs-caption)',
        color: 'var(--alex-text-primary)',
        fontWeight: 500,
      }}
    >
      <span style={{ color: 'var(--alex-text-faint)', display: 'flex' }}>{icon}</span>
      {label}
    </button>
  );
}

function NoMatch() {
  return <div style={{ padding: '6px 14px', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>Tidak ada hasil.</div>;
}
